import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
} from '@mui/material';
import { FC, memo, useEffect, useMemo } from 'react';
import { LaneManualAssignment, ShipmentOrderLaneAutoAssignment, ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';

import { EditableAssignment } from '../hooks/useLaneDetailEdit';

import LaneManualAssignments from './LaneManualAssignments';

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing?: boolean;
  requiredTruckCount?: number | null;
  manualAssignments?: EditableAssignment[];
  fallbackAutoAssignments?: ShipmentOrderLaneAutoAssignment[];
  onRequiredTruckCountChange?: (value: number | null) => void;
  onUpdateAssignment?: (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => void;
  onAddAssignment?: () => void;
  onRemoveAssignment?: (tempId: string) => void;
  onDeleteAssignment?: (assignment: EditableAssignment) => void;
  setTabValue: (value: number) => void;
  startOn: string;
};

/**
 * レーン詳細 - 配車情報タブ
 */
const LaneDetailAssignment: FC<Props> = memo(({
  detail,
  isEditing = false,
  requiredTruckCount,
  manualAssignments,
  fallbackAutoAssignments,
  onRequiredTruckCountChange,
  onUpdateAssignment,
  onAddAssignment,
  onDeleteAssignment,
  setTabValue,
  startOn,
}) => {
  const { data: partnerCompanyData } = usePartnerCompaniesRequest(0, '', '', true, 5000);
  const { data: driverData } = useDriversQuery(startOn, { enabled: isEditing });

  // 未配車判定
  const isUnassigned = detail.laneStatus === 'not_planned'
    && (detail.requiredTruckCount === null || detail.requiredTruckCount === 0);

  // 表示用データ（編集時は編集データ、それ以外は詳細データを使用）
  const displayAssignments = isEditing
    ? manualAssignments || []
    : (detail.laneManualAssignments || []).map((a) => ({ ...a, tempId: `display-${a.id}` }));

  // 未配車時に編集開始で1行自動生成
  useEffect(() => {
    if (isUnassigned && isEditing && (!manualAssignments || manualAssignments.length === 0)) {
      onAddAssignment?.();
    }
  }, [isUnassigned, isEditing, manualAssignments?.length, onAddAssignment]);

  // 自動配車情報をEditableAssignment形式に変換（配車済み時の表示用）
  // detail.autoAssignments が未取得の場合は一覧データのフォールバックを使用
  const rawAutoAssignments = detail.autoAssignments ?? fallbackAutoAssignments ?? [];

  // 自動配車を truckNumber でグループ化（1台1カード）
  const autoByTruck = rawAutoAssignments.reduce<Record<string, typeof rawAutoAssignments>>((acc, a) => {
    const key = a.truckNumber || String(a.deliveryId) || `no-truck-${a.id}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  // legRunId → 実際の物量マップ（detail.shipmentOrders から構築）
  const legRunVolumeMap = useMemo(() => {
    const map = new Map<number, { quantity: number; weightKg: number; volumeM3: number }>();
    (detail.shipmentOrders || []).forEach((order) => {
      (order.shipmentOrderLegs || []).forEach((leg) => {
        (leg.shipmentOrderLegRuns || []).forEach((run) => {
          const quantity = (run.items || []).reduce((sum, item) => sum + (item.quantity ?? 0), 0);
          const weightKg = (run.items || []).reduce((sum, item) => sum + (item.weightKg ?? 0), 0);
          const volumeM3 = (run.items || []).reduce((sum, item) => sum + (item.volumeM3 ?? 0), 0);
          map.set(run.id, { quantity, weightKg, volumeM3 });
        });
      });
    });
    return map;
  }, [detail.shipmentOrders]);

  // トラックごとに実際のlegRunの物量を合算
  const autoAssignmentItems: EditableAssignment[] = Object.values(autoByTruck).map((assignments, i) => {
    const first = assignments[0];
    const deliveryCount = assignments.length;
    const legRunIds = assignments.map((a) => a.id);

    const truckVolume = legRunIds.reduce(
      (acc, id) => {
        const v = legRunVolumeMap.get(id);
        if (!v) return acc;
        return {
          quantity: acc.quantity + v.quantity,
          weightKg: acc.weightKg + v.weightKg,
          volumeM3: acc.volumeM3 + v.volumeM3,
        };
      },
      { quantity: 0, weightKg: 0, volumeM3: 0 },
    );

    return {
      id: undefined,
      tempId: `auto-${first.id}`,
      sequenceNumber: displayAssignments.length + i + 1,
      quantity: truckVolume.quantity,
      weightG: null,
      weightKg: Math.round(truckVolume.weightKg * 1000) / 1000,
      volumeMm3: null,
      volumeM3: Math.round(truckVolume.volumeM3 * 1000) / 1000,
      carrierCompany: first.carrierCompany,
      operatingCarrier: first.operatingCarrier,
      driverName: first.driverName,
      truckNumber: first.truckNumber,
      hierarchyLevel: first.hierarchyLevel ? Number(first.hierarchyLevel) : null,
      deliveryCount,
      isAutoAssignment: true,
      legRunIds,
    };
  });

  // 未配車: 常に1行表示 / 配車済み: 手動配車 + 自動配車を合わせて表示
  const unassignedBase = displayAssignments.length > 0
    ? [displayAssignments[0]]
    : [{ tempId: 'unassigned-placeholder', sequenceNumber: 1 } as EditableAssignment];

  const effectiveAssignments = isUnassigned
    ? unassignedBase.map((a) => ({
        ...a,
        quantity: a.quantity ?? detail.volumeSummary.packages,
        weightKg: a.weightKg ?? detail.volumeSummary.weightKg,
        volumeM3: a.volumeM3 ?? detail.volumeSummary.volumeM3,
      }))
    : [...displayAssignments, ...autoAssignmentItems];

  // 未配車: isEditing に従う / 配車済み: 常に読み取り専用
  const assignmentsEditable = isUnassigned ? isEditing : false;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* 配車サマリー */}
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          配車管理
        </Typography>
        <Box sx={{ gap: 4, border: '1px solid', borderRadius: 1, p: 2, justifyItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="primary.main" mb={2} textAlign="center">
              レーン全体物量
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, 180px)' },
              gridAutoRows: '70px',
            }}
          >
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.packages.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  数量
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.weightKg.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  重量(kg)
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {detail.volumeSummary.volumeM3.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  体積(m3)
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 車両スロット一覧 */}
        {effectiveAssignments.length > 0 && (
          <LaneManualAssignments
            detail={detail}
            isEditing={assignmentsEditable}
            manualAssignments={effectiveAssignments}
            onUpdateAssignment={onUpdateAssignment}
            onDeleteAssignment={onDeleteAssignment}
            setTabValue={setTabValue}
            partnerCompanyData={partnerCompanyData}
            driverData={driverData}
          />
        )}
      </Paper>
    </Box>
  );
});

export default LaneDetailAssignment;
